import {Backdrop, Box, Button, DialogActions, Fade, Grid, Modal, styled, TextField, Typography} from '@mui/material';
import {CatalogSection, Section} from '../../../../api/models';
import {useState} from 'react';
import LinearProgressIndicator from '../LinearProgressIndicator';
import {CloudUploadRounded} from '@mui/icons-material';
import {useCatalogSectionUpdates} from '../../../../api/catalog';
import {CatalogueItemsList} from './CatalogueItemsList';

interface Props {
    section: Section;
    websiteId: string;
    throwSnackbarMessage: (message: string) => void;
    refetch: VoidFunction;
}

const BackgroundBoxWrapper = styled(Box)<{backgroundColor: string}>(({backgroundColor}) => ({
    paddingLeft: 54,
    paddingRight: 54,
    paddingBottom: 32,
    margin: 0,
    backgroundColor: backgroundColor,
}));

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const AddNewCatalogItem = ({
    createCatalogItemBottomSheet,
    setCreateCatalogItemBottomSheet,
}: {
    createCatalogItemBottomSheet: boolean;
    setCreateCatalogItemBottomSheet: (newState: boolean) => void;
}) => {
    const [titleText, updateTitleText] = useState<string | undefined>(undefined);
    const [descriptionText, updateDescriptionText] = useState<string | undefined>(undefined);
    const [priceText, updatePriceText] = useState<string | undefined>(undefined);
    const [uploadProgress, updateUploadProgress] = useState<number | undefined>(undefined);
    const [imageFile, uploadImageFile] = useState<File | undefined>(undefined);

    const {addCatalogItem} = useCatalogSectionUpdates();

    const resetModalState = () => {
        setCreateCatalogItemBottomSheet(false);
        updateTitleText(undefined);
        updateDescriptionText(undefined);
        updatePriceText(undefined);
    };

    return (
        <>
            <div
                onClick={() => {
                    setCreateCatalogItemBottomSheet(true);
                }}>
                <Box>
                    <Typography
                        component={'div'}
                        variant={'body2'}
                        style={{
                            color: 'black',
                            fontWeight: 500,
                            fontFamily: 'Poppins',
                            marginBottom: 12,
                            textDecoration: 'underline',
                        }}>
                        Add New Item
                    </Typography>
                </Box>
            </div>
            <div>
                <Modal
                    aria-labelledby='transition-modal-title'
                    aria-describedby='transition-modal-description'
                    open={createCatalogItemBottomSheet}
                    onClose={() => {
                        setCreateCatalogItemBottomSheet(false);
                    }}
                    closeAfterTransition
                    slots={{backdrop: Backdrop}}
                    slotProps={{
                        backdrop: {
                            timeout: 500,
                        },
                    }}>
                    <Fade in={createCatalogItemBottomSheet}>
                        <Box
                            sx={{
                                position: 'absolute' as 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 400,
                                bgcolor: 'white',
                                boxShadow: 24,
                                borderRadius: 2,
                                paddingLeft: 4,
                                paddingRight: 4,
                                paddingTop: 4,
                                paddingBottom: 2,
                            }}>
                            <Typography id='transition-modal-title' variant='h6' component='h2'>
                                Add new catalog item
                            </Typography>
                            <Typography id='transition-modal-description' sx={{mt: 2}}>
                                Fill in the following details to create a new catalog item in your catalog
                            </Typography>
                            <br />
                            <Grid style={{marginTop: 12}} container>
                                <Grid item md={12} xs={12} sm={12}>
                                    <TextField
                                        label='Title'
                                        style={{
                                            color: 'black',
                                        }}
                                        variant='outlined'
                                        value={titleText}
                                        fullWidth
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            updateTitleText(e.target.value);
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid style={{marginTop: 12}} container>
                                <Grid item md={12} xs={12} sm={12}>
                                    <TextField
                                        label='Description'
                                        style={{
                                            color: 'black',
                                        }}
                                        variant='outlined'
                                        value={descriptionText}
                                        multiline={true}
                                        fullWidth
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            updateDescriptionText(e.target.value);
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Grid style={{marginTop: 12}} container>
                                <Grid item md={12} xs={12} sm={12}>
                                    <TextField
                                        label='Price'
                                        style={{
                                            color: 'black',
                                        }}
                                        variant='outlined'
                                        type='number'
                                        value={priceText}
                                        multiline={true}
                                        fullWidth
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                            updatePriceText(e.target.value);
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <br />
                            <Typography id='transition-modal-description' sx={{mt: 2}}>
                                Select an image for this product
                            </Typography>
                            <center>
                                {uploadProgress !== undefined ? (
                                    <LinearProgressIndicator progress={uploadProgress} />
                                ) : (
                                    <>
                                        {imageFile ? (
                                            <img height={100} width={100} src={URL.createObjectURL(imageFile)} />
                                        ) : null}
                                        <br />
                                        <Button
                                            component='label'
                                            role={undefined}
                                            variant='contained'
                                            tabIndex={-1}
                                            startIcon={<CloudUploadRounded />}>
                                            Product image
                                            <VisuallyHiddenInput
                                                type='file'
                                                accept='.jpg,.jpeg,.png'
                                                onChange={(event) => {
                                                    if (event.target.files !== null && event.target.files?.length > 0) {
                                                        const newImageFile = event.target.files[0];
                                                        uploadImageFile(newImageFile);
                                                    } else {
                                                        resetModalState();
                                                    }
                                                }}
                                                multiple={false}
                                            />
                                        </Button>
                                    </>
                                )}
                            </center>
                            <br />
                            <DialogActions>
                                <Button
                                    onClick={() => {
                                        if (imageFile && titleText && descriptionText && priceText) {
                                            addCatalogItem({
                                                data: {
                                                    file: imageFile,
                                                    title: titleText,
                                                    subtitle: descriptionText,
                                                    price: priceText,
                                                    updateUploadProgress: (progress) => updateUploadProgress(progress),
                                                    action: 'ADD',
                                                },
                                                throwSnackbarMessage: (msg) => {
                                                    console.info('LOG ADD NEW CATALOG ITEM', msg);
                                                },
                                            });
                                        } else {
                                            console.info('ERROR');
                                        }
                                    }}>
                                    Save changes
                                </Button>
                                <Button onClick={resetModalState}>Dismiss</Button>
                            </DialogActions>
                        </Box>
                    </Fade>
                </Modal>
            </div>
        </>
    );
};

export const CatalogCompanionComponent = (props: Props) => {
    const [createCatalogItemBottomSheet, setCreateCatalogItemBottomSheet] = useState(false);

    const catalogSection = props.section as CatalogSection;

    return (
        <BackgroundBoxWrapper backgroundColor='white'>
            <AddNewCatalogItem
                createCatalogItemBottomSheet={createCatalogItemBottomSheet}
                setCreateCatalogItemBottomSheet={(newVal) => {
                    setCreateCatalogItemBottomSheet(newVal);
                }}
            />
            <br />
            <CatalogueItemsList catalogItems={catalogSection.catalogComponents} />
        </BackgroundBoxWrapper>
    );
};
